/**
 * Builds the Typesense `filter_by` string from a search module's active preset
 * filters, so the SSR / no-JS job-link fetch (see useSsrJobLinks) requests the
 * same offer subset the module renders for real users.
 *
 * This mirrors the `resolvedPresetFilters` computed in the crossroads
 * `searchAndFilterModule` mixin
 * (@jobshop/crossroads/components/SearchAndFilter/mixins/searchAndFilterModule.js)
 * — the mixin is only reachable via the Options API `this`, not from `setup()`,
 * so the logic is duplicated here and must be kept in sync with it. The
 * backtick-doubling escape (rather than a backslash) is required by Typesense's
 * filter tokenizer — see JET-2182. (JET-1826)
 */
export function resolvePresetFilters(config: SearchConfig): string {
  const filters = config?.filters
  if (!filters || !filters.options)
    return ""

  return filters.options
    .filter(option => option.active && option.preset.length > 0)
    .map(option => `${option.key}:=[${option.preset.map(value => `\`${value.replace(/`/g, "``")}\``).join(", ")}]`)
    .join(" && ")
}

/**
 * Appends the configured nationwide value to a `location:=[…]` clause in a
 * `filter_by` string, so nationwide offers match when a location is filtered —
 * mirroring `checkLocationFilter` in the crossroads location-filter helpers
 * (@jobshop/crossroads/utils/search/handleLocationFilters.js), which the live
 * search runs on every request. No-op when there is no location clause or the
 * nationwide value is already present. Kept in sync with that helper. (JET-1826)
 */
export function addNationwideToLocationFilter(filterBy: string, nationwideText: string): string {
  if (!filterBy || !nationwideText || !filterBy.includes("location:"))
    return filterBy

  const match = filterBy.match(/location:=\[([^\]]+)]/)
  if (!match || !match[1] || match[1].includes(`\`${nationwideText}\``))
    return filterBy

  return filterBy.replace(match[0], match[0].replace("]", `, \`${nationwideText}\`]`))
}

/**
 * Full `filter_by` string for a search module's SSR job-link fetch: its active
 * preset filters plus the nationwide-location augmentation the live search
 * applies. Use this (not resolvePresetFilters alone) for parity with the
 * module's rendered results. (JET-1826)
 */
export function resolveModuleFilterBy(config: SearchConfig): string {
  return addNationwideToLocationFilter(resolvePresetFilters(config), config?.filters?.nationwideText ?? "")
}
